import { type ComponentProps, useCallback, useMemo } from 'react';
import { type Tabs } from 'expo-router';

import { type TabBarButtonExtraProps } from '@sg/garnish';

import { useTelemetry } from '@order/Telemetry';

import { useRouteTitles } from '../../../messages';

// ─────────────────────────────────────────────────────────────────────────────

export const useTabOptions = () => {
  const { track } = useTelemetry();
  const titles = useRouteTitles();

  // ─── Telemetry ───────────────────────────────────────────────────────

  const trackHomeTabPress = useCallback(() => {
    track('tab-bar.home');
  }, [track]);

  const trackMenuTabPress = useCallback(() => {
    track('tab-bar.menu');
  }, [track]);

  const trackLoyaltyTabPress = useCallback(() => {
    track('tab-bar.loyalty');
  }, [track]);

  const trackReorderTabPress = useCallback(() => {
    track('tab-bar.reorder');
  }, [track]);

  const trackGiftCardsTabPress = useCallback(() => {
    track('tab-bar.gift');
  }, [track]);

  // ─── Per-Tab Options ──────────────────────────────────────────────

  const homeTabOptions = useMemo<TabBarButtonOptions>(
    () => ({
      title: titles.homeTitle,
      customDocumentTitle: titles.homeCustomDocumentTitle,
      tabBarTestID: 'nav-bar.home',
      iconName: 'IconHomeAlternative',
      onPress: trackHomeTabPress,
    }),
    [titles.homeTitle, titles.homeCustomDocumentTitle, trackHomeTabPress],
  );

  const menuTabOptions = useMemo<TabBarButtonOptions>(
    () => ({
      title: titles.locationsTitle,
      tabBarTestID: 'nav-bar.order',
      iconName: 'IconMenuBowl',
      onPress: trackMenuTabPress,
    }),
    [titles.locationsTitle, trackMenuTabPress],
  );

  const loyaltyTabOptions = useMemo<TabBarButtonOptions>(
    () => ({
      title: titles.loyaltyTitle,
      customDocumentTitle: titles.loyaltyCustomDocumentTitle,
      tabBarTestID: 'nav-bar.loyalty',
      iconName: 'IconSgRewardBlackOutline',
      onPress: trackLoyaltyTabPress,
    }),
    [titles.loyaltyTitle, titles.loyaltyCustomDocumentTitle, trackLoyaltyTabPress],
  );

  const reorderTabOptions = useMemo<TabBarButtonOptions>(
    () => ({
      title: titles.reorderTitle,
      customDocumentTitle: titles.reorderCustomDocumentTitle,
      tabBarTestID: 'nav-bar.reorder',
      iconName: 'IconHeartStroke',
      onPress: trackReorderTabPress,
    }),
    [titles.reorderTitle, titles.reorderCustomDocumentTitle, trackReorderTabPress],
  );

  const giftTabOptions = useMemo<TabBarButtonOptions>(
    () => ({
      title: titles.giftCardsTabTitle,
      tabBarTestID: 'nav-bar.gift-cards',
      iconName: 'IconPromo',
      popToTopOnBlur: true,
      onPress: trackGiftCardsTabPress,
    }),
    [titles.giftCardsTabTitle, trackGiftCardsTabPress],
  );

  const accountTabOptions = useMemo<TabBarButtonOptions>(
    () => ({ href: null, popToTopOnBlur: true }),
    [],
  );

  // ─────────────────────────────────────────────────────────────────────

  return {
    homeTabOptions,
    menuTabOptions,
    loyaltyTabOptions,
    reorderTabOptions,
    giftTabOptions,
    accountTabOptions,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type TabScreenOptions = NonNullable<ComponentProps<typeof Tabs.Screen>['options']>;

type TabBarButtonOptions = TabScreenOptions & TabBarButtonExtraProps;
